import { memo, ReactNode } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { LoadingButton, LoadingButtonProps } from '@mui/lab'

export type ButtonSize = 'large' | 'medium' | 'small' | 'tiny'
export type ButtonColor = 'primary' | 'light' | 'dark' | 'grey' | 'black' | 'white'

interface Props extends Omit<LoadingButtonProps, 'color' | 'size'> {
  children?: string
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  size?: ButtonSize
  color?: ButtonColor
  round?: boolean
  selected?: boolean
  className?: any
}

const sizeStyles = {
  large: tw`h-[56px]`,
  medium: tw`h-[42px]`,
  small: tw`h-[36px]`,
  tiny: tw`h-[28px]`,
}

const colorsStyles = {
  primary: tw`bg-primary-500 hover:bg-primary-700 active:bg-primary-700`,
  light: tw`bg-primary-100 hover:bg-primary-200 active:bg-primary-400`,
  dark: tw`bg-primary-700 hover:bg-primary-800 active:bg-shades-1000`,
  grey: tw`bg-shades-100 hover:bg-shades-200 active:bg-shades-300`,
  black: tw`bg-shades-1000 hover:bg-primary-800 active:bg-primary-800`,
  white: tw`bg-shades-0 hover:bg-shades-100 active:bg-shades-200`,
}

const selectedColorsStyles = {
  primary: tw`bg-primary-700`,
  light: tw`bg-primary-400`,
  dark: tw`bg-shades-1000`,
  grey: tw`bg-shades-300`,
  black: tw`bg-primary-800`,
  white: tw`bg-shades-200`,
}

export const iconStyles = {
  primary: tw`fill-shades-0`,
  light: tw`fill-primary-500`,
  dark: tw`fill-shades-0`,
  grey: tw`fill-shades-1000`,
  black: tw`fill-shades-0`,
  white: tw`fill-shades-1000`,
}

const textColorsStyles = {
  primary: tw`text-shades-0`,
  light: tw`text-primary-500`,
  dark: tw`text-shades-0`,
  grey: tw`text-shades-1000`,
  black: tw`text-shades-0`,
  white: tw`text-shades-1000`,
}

const textSizeStyles = {
  large: tw`text-[16px]`,
  medium: tw`text-[16px]`,
  small: tw`text-[14px]`,
  tiny: tw`text-[14px]`,
}

const ButtonComponent = ({
  children,
  leftIcon,
  rightIcon,
  className,
  round,
  selected,
  disabled,
  loading,
  size = 'large',
  color = 'primary',
  ...rest
}: Props) => (
  <LoadingButton
    {...rest}
    loading={loading}
    disabled={disabled}
    css={[
      tw`min-w-0 px-4`,
      !children && tw`px-2`,
      sizeStyles[size],
      colorsStyles[color],
      selected && selectedColorsStyles[color],
      round && tw`rounded-[40px]`,
      disabled && tw`opacity-50`,
      className,
    ]}
  >
    {leftIcon && !loading ? (
      <div css={[tw`mr-2 h-5`, !children && tw`mr-0`]}>{leftIcon}</div>
    ) : (
      <div css={children && tw`w-1`} />
    )}
    {children && (
      <Typography
        css={[
          tw`font-semibold`,
          textColorsStyles[color],
          textSizeStyles[size],
          loading && tw`opacity-0`,
        ]}
      >
        {children}
      </Typography>
    )}
    {rightIcon && !loading ? (
      <div css={[tw`ml-2 h-5`, !children && tw`ml-0`]}>{rightIcon}</div>
    ) : (
      <div css={children && tw`w-1`} />
    )}
  </LoadingButton>
)

export const Button = memo(ButtonComponent)
